import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Plus, XIcon } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Keyboard, ScrollView } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { twJoin } from 'tailwind-merge';
import { z } from 'zod';

import { getCancelHeaderProps, headerItem } from '@/components/HeaderActions';
import { Header } from '@/components/header';
import { Alert, AlertText } from '@/components/ui/alert';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Icon } from '@/components/ui/icon';
import {
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useConfirmDiscard } from '@/components/useConfirmDiscard';
import { WorkHistoryEntry } from '@/components/workHistoryEntry';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ROUTES } from '@/constants/routes';
import { useGoBack } from '@/hooks/useGoBack';
import { useToaster } from '@/hooks/useToaster';
import { getUserV2QueryOptions } from '@/services/getUserV2';
import { userIdAtom } from '@/services/globalState';
import { updateUser, UpdateUserV2Request } from '@/services/updateUser';
import { segmentClient } from '@/util/analytics';
import {
  WorkHistoryFormValues,
  WorkHistoryEntry as WorkHistoryEntryType,
  isEntryEmpty,
  sortByYear,
  validateYear,
  validateYearRange,
} from '@/utils/profileHelpers';

const workHistoryEntrySchema = z
  .object({
    positionTitle: z.string().optional(),
    organization: z.string().min(1, 'Organization is required'),
    roleDescription: z.string().optional(),
    workLocation: z
      .object({
        city: z.string().optional(),
        state: z.string().optional(),
      })
      .optional(),
    startYear: z.string().optional().or(z.literal('')).refine(validateYear, {
      message: 'Enter 4 digits',
    }),
    endYear: z.string().optional().or(z.literal('')).refine(validateYear, {
      message: 'Enter 4 digits',
    }),
    isPresent: z.boolean().optional(),
  })
  .refine(
    (data) => {
      return validateYearRange(data.startYear, data.endYear, data.isPresent || false);
    },
    {
      message: 'End year must be after start',
      path: ['endYear'],
    }
  );

const workHistorySchema = z.object({
  workHistory: z.array(workHistoryEntrySchema),
});

type WorkHistoryPayloadEntry = NonNullable<UpdateUserV2Request['workHistory']>[number];

const toApiWorkHistory = (entry: WorkHistoryEntryType): WorkHistoryPayloadEntry => ({
  positionTitle: entry.positionTitle || '',
  organization: entry.organization || '',
  roleDescription: entry.roleDescription || undefined,
  workLocation:
    entry.workLocation.city || entry.workLocation.state
      ? {
          city: entry.workLocation.city || undefined,
          state: entry.workLocation.state || undefined,
        }
      : undefined,
  startDate: entry.startYear || '',
  endDate: entry.isPresent ? '' : entry.endYear || '',
  isPresent: entry.isPresent || false,
});

function EditWorkHistoryScreen() {
  const userId = useAtomValue(userIdAtom);

  const goBack = useGoBack(ROUTES.ACCOUNT_PROFILE);

  const queryClient = useQueryClient();

  const toaster = useToaster();

  const { data: userData, isLoading } = useQuery(getUserV2QueryOptions(userId));

  const { mutateAsync: updateUserMutation } = useMutation({ mutationFn: updateUser });

  const form = useForm<WorkHistoryFormValues>({
    defaultValues: {
      workHistory: [],
    },
    resolver: zodResolver(workHistorySchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'workHistory',
  });

  const [expandedItem, setExpandedItem] = useState<string[]>([]);

  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);

  const [deleting, setDeleting] = useState(false);

  const scrollRef = useRef(null);

  const roleDescRefs = useRef<any[]>([]);

  const [formError, setFormError] = useState<string | null>(null);

  const workHistoryErrorCount = Array.isArray(form.formState.errors.workHistory)
    ? form.formState.errors.workHistory.filter((error) => error).length
    : 0;

  const onSubmit = async () => {
    try {
      setFormError(null);

      const isValid = await form.trigger();

      if (!isValid) {
        const workHistoryErrors = form.formState.errors.workHistory || [];

        const errorCount = Array.isArray(workHistoryErrors)
          ? workHistoryErrors.filter((error) => error).length
          : 0;

        const errorMessage = `Found ${errorCount} ${errorCount === 1 ? 'entry' : 'entries'} with errors`;

        setFormError(`Please fix the errors in the form: ${errorMessage}`);

        return;
      }

      setFormError(null);

      Keyboard.dismiss();

      const values = form.getValues();

      if (!values || !values.workHistory) {
        throw new Error('Form values missing workHistory data');
      }

      const filteredWorkHistory = values.workHistory.filter((entry) => !isEntryEmpty(entry));

      if (filteredWorkHistory.length === 0) {
        throw new Error('No valid work history entries to save');
      }

      const workHistoryPayload = filteredWorkHistory.map(toApiWorkHistory);

      try {
        await updateUserMutation({
          workHistory: workHistoryPayload,
        });

        const nonEmpty = (values.workHistory || []).filter((entry) => {
          return (
            entry.positionTitle?.trim() ||
            entry.organization?.trim() ||
            entry.roleDescription?.trim() ||
            entry.workLocation?.city?.trim() ||
            entry.workLocation?.state?.trim() ||
            entry.startYear?.trim() ||
            entry.endYear?.trim()
          );
        });

        form.reset({ workHistory: nonEmpty }, { keepValues: true, keepDirty: false });

        queryClient.invalidateQueries();

        segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_SAVED, {
          section: 'work_history',
          previous: userData,
          current: form.getValues(),
        });

        toaster.showSuccess({ title: 'Work history updated' });

        goBack();
      } catch (apiError) {
        throw new Error(
          `API error: ${apiError instanceof Error ? apiError.message : 'Unknown API error'}`
        );
      }
    } catch (error) {
      segmentClient.track(ANALYTICS_EVENTS.PROFILE_EDIT_FAILED, {
        section: 'work_history',
        previous: userData,
        current: form.getValues(),
      });

      toaster.showError({
        title: 'Failed to save work history',
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    }
  };

  useEffect(() => {
    if (userData) {
      const workHistoryEntries = (userData.workHistory || []).map((entry) => ({
        positionTitle: entry.positionTitle ?? '',
        organization: entry.organization ?? '',
        roleDescription: entry.roleDescription ?? '',
        workLocation: {
          city: entry.workLocation?.city ?? '',
          state: entry.workLocation?.state ?? '',
        },
        startYear: entry.startDate ?? '',
        endYear: entry.endDate ?? '',
        isPresent: entry.isPresent ?? false,
      }));

      form.reset({
        workHistory: sortByYear(workHistoryEntries),
      });
    }
  }, [userData]);

  const handleBack = () => {
    goBack();
  };

  const { confirmCancel, discardModal } = useConfirmDiscard({
    isDirty: form.formState.isDirty,
    onDiscard: handleBack,
  });

  const handleDeleteRequest = (index: number) => {
    setPendingDeleteIndex(index);

    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteIndex == null) {
      return;
    }

    setDeleting(true);

    try {
      const currentWorkHistory = form.getValues().workHistory;

      const updatedWorkHistory = [...currentWorkHistory];

      updatedWorkHistory.splice(pendingDeleteIndex, 1);

      const workHistoryPayload = updatedWorkHistory.map(toApiWorkHistory);

      await updateUserMutation({
        workHistory: workHistoryPayload,
      });

      queryClient.invalidateQueries();

      toaster.showSuccess({ title: 'Work history entry deleted' });

      remove(pendingDeleteIndex);

      setShowDeleteModal(false);

      setPendingDeleteIndex(null);
    } catch (error) {
      toaster.showError({
        title: 'Failed to delete entry',
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    } finally {
      setDeleting(false);
    }
  };

  const addEmptyEntry = () => {
    const emptyEntry: WorkHistoryEntryType = {
      positionTitle: '',
      organization: '',
      roleDescription: '',
      workLocation: { city: '', state: '' },
      startYear: '',
      endYear: '',
      isPresent: false,
    };

    append(emptyEntry, { shouldFocus: false });

    const newIndex = fields.length;

    setExpandedItem([`item-${newIndex}`]);
  };

  if (isLoading) {
    return <Spinner size="large" className="flex-1" />;
  }

  return (
    <VStack
      className={
        isWeb ? 'mx-auto w-full max-w-screen-md flex-1 px-5 md:px-10' : 'flex-1 bg-background-0'
      }>
      <Header
        contentScrollsUnderHeader
        {...getCancelHeaderProps(confirmCancel)}
        title="Edit Work History"
        rightItems={[
          headerItem.save({
            onPress: form.handleSubmit(onSubmit),
            isDisabled: !form.formState.isDirty,
            isSaving: form.formState.isSubmitting,
          }),
        ]}
      />

      {discardModal}
      {!isWeb ? (
        <KeyboardAwareScrollView
          contentInsetAdjustmentBehavior="automatic"
          mode="layout"
          ref={scrollRef}
          className="flex-1"
          contentContainerStyle={{ paddingBottom: 32, paddingTop: 0 }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          enabled
          extraKeyboardSpace={120}>
          <VStack space="sm" className="px-6 pt-6">
            {/* Global error banner */}
            {workHistoryErrorCount > 0 && (
              <Alert action="error" className="mx-4 my-2">
                <AlertText size="md">
                  {`Complete required fields in ${workHistoryErrorCount} work history entr${workHistoryErrorCount === 1 ? 'y' : 'ies'} before saving.`}
                </AlertText>
              </Alert>
            )}
            {fields.map((field, index) => (
              <WorkHistoryEntry
                key={field.id}
                field={field}
                index={index}
                form={form}
                onDeleteRequest={handleDeleteRequest}
                expandedItem={expandedItem}
                setExpandedItem={setExpandedItem}
                scrollRef={scrollRef}
                roleDescRefs={roleDescRefs}
                hasError={
                  !!(form.formState.errors.workHistory && form.formState.errors.workHistory[index])
                }
              />
            ))}
            <Button variant="outline" size="sm" onPress={addEmptyEntry} className="mt-4 self-start">
              <Icon as={Plus} size="sm" className="mr-2 text-typography-500" />
              <ButtonText>Add Work History</ButtonText>
            </Button>
          </VStack>
        </KeyboardAwareScrollView>
      ) : (
        <ScrollView
          ref={scrollRef}
          className="flex-1"
          contentContainerStyle={{ paddingBottom: 32, paddingTop: 0 }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag">
          <VStack space="sm" className="px-6 pt-6">
            {/* Global error banner */}
            {workHistoryErrorCount > 0 && (
              <Alert action="error" className="mx-4 my-2">
                <AlertText size="md">
                  {`Complete required fields in ${workHistoryErrorCount} work history entr${workHistoryErrorCount === 1 ? 'y' : 'ies'} before saving.`}
                </AlertText>
              </Alert>
            )}
            {fields.map((field, index) => (
              <WorkHistoryEntry
                key={field.id}
                field={field}
                index={index}
                form={form}
                onDeleteRequest={handleDeleteRequest}
                expandedItem={expandedItem}
                setExpandedItem={setExpandedItem}
                scrollRef={scrollRef}
                roleDescRefs={roleDescRefs}
                hasError={
                  !!(form.formState.errors.workHistory && form.formState.errors.workHistory[index])
                }
              />
            ))}
            <Button variant="outline" size="sm" onPress={addEmptyEntry} className="mt-4 self-start">
              <Icon as={Plus} size="sm" className="mr-2 text-typography-500" />
              <ButtonText>Add Work History</ButtonText>
            </Button>
          </VStack>
        </ScrollView>
      )}

      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="md">
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader>
            <Heading size="h2-xl">Delete entry</Heading>
            <ModalCloseButton onPress={() => setShowDeleteModal(false)}>
              <Icon as={XIcon} />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            <Text>Do you want to proceed?</Text>
          </ModalBody>
          <ModalFooter className="w-full flex-col justify-start gap-4">
            <Button
              disabled={deleting}
              className={twJoin('w-full', deleting && 'opacity-50')}
              onPress={handleConfirmDelete}>
              {deleting ? <Spinner size="small" /> : <ButtonText>Delete entry</ButtonText>}
            </Button>
            <Button
              className="w-full"
              variant="outline"
              action="secondary"
              onPress={() => setShowDeleteModal(false)}>
              <ButtonText>Continue editing</ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={showDiscardModal} onClose={() => setShowDiscardModal(false)} size="md">
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader>
            <Heading size="h2-xl">Discard changes</Heading>
            <ModalCloseButton onPress={() => setShowDiscardModal(false)}>
              <Icon as={XIcon} />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            <Text>Do you want to proceed?</Text>
          </ModalBody>
          <ModalFooter className="w-full flex-col justify-start gap-4">
            <Button
              className="w-full"
              onPress={() => {
                setShowDiscardModal(false);

                goBack();
              }}>
              <ButtonText>Discard changes</ButtonText>
            </Button>
            <Button
              className="w-full"
              variant="outline"
              action="secondary"
              onPress={() => setShowDiscardModal(false)}>
              <ButtonText>Continue editing</ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

export default EditWorkHistoryScreen;
